/**
 * tokens — personalisation tokens for the message composer and email designer.
 * Approved UX: `Personalisation Tokens - UX Proposal.dc.html` (2026-07-31).
 *
 * The user always writes and sees NAMED tokens on every surface (operator
 * ruling). WhatsApp is the exception underneath, not above: Meta requires
 * numbered placeholders, so `compileForWhatsApp()` converts named → {{1}}, {{2}}
 * at submit time and hands back the mapping to display. One mental model for the
 * user; Meta still gets what it needs.
 *
 * Syntax: {{token}} or {{token | fallback}}. Legacy single-brace {token} rows
 * are recognised too, so nothing already written breaks.
 *
 * The catalog here is PROVISIONAL — REQ-115 asks for a real per-context read.
 * Marked as such in the UI so nobody mistakes it for a contract.
 */
import { useEffect, useLayoutEffect, useRef, useState, type JSX } from "react";
import { createPortal } from "react-dom";
import { alpha, Btn, Icon, T } from "@v0y4ge/jrni-ui";
import { useT } from "../../lib/i18n";
import { analyzeSms, type SmsAnalysis } from "./sms-encoding";

export type TokenType = "text" | "link" | "date" | "number";
export type TokenGroup = "person" | "appointment" | "event" | "queue" | "org";
export interface TokenDef {
  id: string; label: string; labelKey: string; group: TokenGroup; type: TokenType;
  desc: string; sample: string;
  /** This token is the SHORT form of the token named here. The picker renders
   *  the pair as ONE entry with a length choice rather than two unrelated rows;
   *  the vocabulary is two tokens either way, and neither is ever rewritten
   *  behind the author's back (CD prompt 2026-09-01 §4). */
  shortOf?: string;
  /** Why this link deliberately has NO short form. The picker must treat "no
   *  short form" as a normal state rather than an error, so the reason is
   *  surfaced instead of leaving the operator guessing. */
  noShortReason?: string;
  /** Resolved by the platform per recipient, never typed by the author: a Button
   *  may TARGET it, but its URL is not authored. An artefact placeholder also
   *  gates on a setting — it resolves only while its mode is `download link`. */
  systemIssued?: boolean;
  /** Can legitimately be empty for a real recipient → offer a fallback. */
  blankable?: boolean;
}

/* ui-config — provisional vocabulary (REQ-115), not tenant data. */
export const TOKENS: TokenDef[] = [
  { id: "first_name", label: "First name", labelKey: "messaging.token.first_name.label", group: "person", type: "text", desc: "Given name", sample: "Noor", blankable: true },
  { id: "last_name", label: "Last name", labelKey: "messaging.token.last_name.label", group: "person", type: "text", desc: "Family name", sample: "Haddad", blankable: true },
  { id: "full_name", label: "Full name", labelKey: "messaging.token.full_name.label", group: "person", type: "text", desc: "Both names", sample: "Noor Haddad", blankable: true },
  { id: "email", label: "Email", labelKey: "messaging.token.email.label", group: "person", type: "text", desc: "Their email address", sample: "n.haddad@example.com" },
  { id: "mobile", label: "Mobile", labelKey: "messaging.token.mobile.label", group: "person", type: "text", desc: "Their mobile number", sample: "+1 617 555 0148" },
  { id: "preferred_language", label: "Preferred language", labelKey: "messaging.token.preferred_language.label", group: "person", type: "text", desc: "Language on their profile", sample: "English", blankable: true },

  { id: "service", label: "Service", labelKey: "messaging.token.service.label", group: "appointment", type: "text", desc: "What they booked", sample: "Mortgage consultation" },
  { id: "staff", label: "Staff member", labelKey: "messaging.token.staff.label", group: "appointment", type: "text", desc: "Who they are seeing", sample: "Jordan Pierce", blankable: true },
  { id: "date", label: "Date", labelKey: "messaging.token.date.label", group: "appointment", type: "date", desc: "Appointment date", sample: "Tuesday 14 October" },
  { id: "time", label: "Time", labelKey: "messaging.token.time.label", group: "appointment", type: "date", desc: "Appointment start time", sample: "14:30" },
  { id: "duration", label: "Duration", labelKey: "messaging.token.duration.label", group: "appointment", type: "number", desc: "Length in minutes", sample: "45" },
  { id: "location", label: "Location", labelKey: "messaging.token.location.label", group: "appointment", type: "text", desc: "Branch or store name", sample: "Boston — Back Bay" },
  { id: "location_address", label: "Location address", labelKey: "messaging.token.location_address.label", group: "appointment", type: "text", desc: "Full postal address of the appointment", sample: "200 Boylston Street, Boston, MA 02116" },
  { id: "directions_url", label: "Directions link", labelKey: "messaging.token.directions_url.label", group: "appointment", type: "link", desc: "Map link for the location", sample: "https://jrni.example/directions/ab-backbay" },
  { id: "manage_url", label: "Manage booking link", labelKey: "messaging.token.manage_url.label", group: "appointment", type: "link", desc: "Reschedule or cancel", sample: "https://jrni.example/manage/8f2c" },
  /* Two manage tokens, not one, and NEITHER is rewritten per channel (CC 2026-08-31,
     operator decision): which link goes into a customer's message is the tenant's
     choice. Placing the short one is also what MINTS the short link — a template that
     never references it stores nothing. */
  { id: "manage_short_url", label: "Manage link (short)", labelKey: "messaging.token.manage_short_url.label", group: "appointment", type: "link", desc: "Same page, ~39 characters shorter — for SMS", sample: "l.jrni.ai/aK7qP3zM9x", shortOf: "manage_url" },
  { id: "join_url", label: "Video join link", labelKey: "messaging.token.join_url.label", group: "appointment", type: "link", desc: "For virtual appointments", sample: "https://jrni.example/join/8f2c", blankable: true },

  { id: "event_name", label: "Event name", labelKey: "messaging.token.event_name.label", group: "event", type: "text", desc: "Title of the event", sample: "Autumn Advice Evening" },
  { id: "event_date", label: "Event date", labelKey: "messaging.token.event_date.label", group: "event", type: "date", desc: "Date it runs", sample: "Wednesday 15 October" },
  { id: "event_time", label: "Event time", labelKey: "messaging.token.event_time.label", group: "event", type: "date", desc: "Start time", sample: "18:00" },
  { id: "venue", label: "Venue", labelKey: "messaging.token.venue.label", group: "event", type: "text", desc: "Venue name", sample: "Back Bay Auditorium" },
  { id: "venue_address", label: "Venue address", labelKey: "messaging.token.venue_address.label", group: "event", type: "text", desc: "Full postal address of the venue", sample: "200 Boylston Street, Boston, MA 02116" },
  { id: "ticket_url", label: "Ticket link", labelKey: "messaging.token.ticket_url.label", group: "event", type: "link", desc: "Their ticket", sample: "https://jrni.example/ticket/71ba" },
  { id: "register_url", label: "Registration link", labelKey: "messaging.token.register_url.label", group: "event", type: "link", desc: "Sign-up page", sample: "https://jrni.example/e/autumn-advice" },
  { id: "recap_url", label: "Recap link", labelKey: "messaging.token.recap_url.label", group: "event", type: "link", desc: "Post-event summary", sample: "https://jrni.example/recap/71ba" },

  { id: "queue_name", label: "Queue name", labelKey: "messaging.token.queue_name.label", group: "queue", type: "text", desc: "Which queue", sample: "Lobby walk-ins" },
  { id: "position", label: "Position", labelKey: "messaging.token.position.label", group: "queue", type: "number", desc: "Place in line", sample: "4" },
  { id: "est_wait", label: "Estimated wait", labelKey: "messaging.token.est_wait.label", group: "queue", type: "number", desc: "Minutes remaining", sample: "12" },
  { id: "delay", label: "Delay", labelKey: "messaging.token.delay.label", group: "queue", type: "number", desc: "Minutes behind schedule", sample: "20" },
  { id: "to_queue", label: "Moved-to queue", labelKey: "messaging.token.to_queue.label", group: "queue", type: "text", desc: "Destination on a transfer", sample: "Notary desk" },
  { id: "rejoin_url", label: "Rejoin link", labelKey: "messaging.token.rejoin_url.label", group: "queue", type: "link", desc: "Get back in line", sample: "https://jrni.example/q/rejoin/4a1" },

  { id: "org_name", label: "Organisation name", labelKey: "messaging.token.org_name.label", group: "org", type: "text", desc: "Your organisation", sample: "Acme Bank" },
  { id: "org_phone", label: "Organisation phone", labelKey: "messaging.token.org_phone.label", group: "org", type: "text", desc: "Main contact number", sample: "+1 617 555 0100" },
  { id: "org_address", label: "Organisation address", labelKey: "messaging.token.org_address.label", group: "org", type: "text", desc: "Registered address", sample: "200 Boylston Street, Boston, MA 02116" },
  { id: "book_url", label: "Booking link", labelKey: "messaging.token.book_url.label", group: "org", type: "link", desc: "Start a new booking", sample: "https://jrni.example/book" },
  { id: "survey_url", label: "Feedback link", labelKey: "messaging.token.survey_url.label", group: "org", type: "link", desc: "Survey or feedback form", sample: "https://jrni.example/feedback/8f2c" },
  { id: "unsubscribe_url", label: "Unsubscribe link", labelKey: "messaging.token.unsubscribe_url.label", group: "org", type: "link", desc: "Required in marketing email footers", sample: "https://jrni.example/unsubscribe/8f2c", noShortReason: "A recipient must see the link goes to your own domain, not an opaque redirect — for a legal or consent destination the visible URL is the point." },

  /* Artefact URLs (CD prompt 2026-09-01 §4). Both are system-issued: the author
     places the placeholder and the platform resolves it per recipient. Each one
     resolves ONLY while its artefact mode is `download link` — on `off` or
     `attachment` there is no URL, which is why an unresolvable placeholder is
     refused at publish rather than shipped as a button that goes nowhere. */
  { id: "calendar_ics_url", label: "Calendar download", labelKey: "messaging.token.calendar_ics_url.label", group: "event", type: "link", desc: "Add-to-calendar link — needs Calendar set to Download link", sample: "https://jrni.example/cal/71ba.ics", systemIssued: true },
  { id: "wallet_pass_url", label: "Wallet pass", labelKey: "messaging.token.wallet_pass_url.label", group: "person", type: "link", desc: "Apple Wallet pass — needs Wallet pass set to Download link", sample: "https://jrni.example/pass/8f2c", systemIssued: true,
    noShortReason: "The wallet URL carries a credential and a TTL, so it is minted per recipient rather than shortened." },
  /* ── Booking Settings vocabulary (2026-09-08, Nick — Three Pane port). The
     staff, event-operations and walk-in fields the 28 booking message types
     resolve. Same closed vocabulary, same picker; STAFF-ONLY fields say so in
     desc so a customer template author sees why they are the wrong reach. ── */
  { id: "booking_reference", label: "Booking reference", labelKey: "messaging.token.booking_reference.label", group: "appointment", type: "text", desc: "The reference on the confirmation, quoted on every support call", sample: "APT-2417" },
  { id: "called_time", label: "Time called", labelKey: "messaging.token.called_time.label", group: "appointment", type: "date", desc: "When a checked-in customer was called to the room", sample: "10:04" },
  { id: "booking_notes", label: "Booking notes", labelKey: "messaging.token.booking_notes.label", group: "appointment", type: "text", desc: "What the customer wrote when booking — staff only", sample: "Wants to discuss overpayment options", blankable: true },
  { id: "previous_date_time", label: "Previous date and time", labelKey: "messaging.token.previous_date_time.label", group: "appointment", type: "date", desc: "Where the booking was before it moved", sample: "Tuesday 14 October at 11:00" },
  { id: "cancel_reason", label: "Cancellation reason", labelKey: "messaging.token.cancel_reason.label", group: "appointment", type: "text", desc: "The reason given, if any — staff only", sample: "Work commitment", blankable: true },
  { id: "staff_booking_url", label: "Open the booking (staff)", labelKey: "messaging.token.staff_booking_url.label", group: "appointment", type: "link", desc: "Deep link into the staff diary — staff only", sample: "https://jrni.example/s/bk/8f2c" },
  { id: "registration_reference", label: "Registration reference", labelKey: "messaging.token.registration_reference.label", group: "event", type: "text", desc: "The reference on the event registration", sample: "EVT-71BA-0112" },
  { id: "checkin_url", label: "Check-in link", labelKey: "messaging.token.checkin_url.label", group: "event", type: "link", desc: "Self check-in on arrival", sample: "https://jrni.example/e/71ba/checkin" },
  { id: "claim_url", label: "Claim place link", labelKey: "messaging.token.claim_url.label", group: "event", type: "link", desc: "Claims an opened waitlist place", sample: "https://jrni.example/e/71ba/claim/4a1" },
  { id: "offer_expires", label: "Offer expires", labelKey: "messaging.token.offer_expires.label", group: "event", type: "date", desc: "When an opened waitlist place is released again", sample: "Thursday 16 October at 18:00" },
  { id: "capacity", label: "Capacity", labelKey: "messaging.token.capacity.label", group: "event", type: "number", desc: "Places the venue holds — staff only", sample: "120" },
  { id: "registration_count", label: "Registrations", labelKey: "messaging.token.registration_count.label", group: "event", type: "number", desc: "Places taken so far — staff only", sample: "112" },
  { id: "places_remaining", label: "Places remaining", labelKey: "messaging.token.places_remaining.label", group: "event", type: "number", desc: "Places still open — staff only", sample: "8" },
  { id: "minimum_registrations", label: "Minimum registrations", labelKey: "messaging.token.minimum_registrations.label", group: "event", type: "number", desc: "Below this the event does not run — staff only", sample: "20" },
  { id: "attendee_list_url", label: "Attendee list (staff)", labelKey: "messaging.token.attendee_list_url.label", group: "event", type: "link", desc: "Check-in list for the door — staff only", sample: "https://jrni.example/e/71ba/list" },
  { id: "desk", label: "Desk", labelKey: "messaging.token.desk.label", group: "queue", type: "text", desc: "Where a called customer goes", sample: "Desk 3" },
  { id: "waiting_count", label: "People waiting", labelKey: "messaging.token.waiting_count.label", group: "queue", type: "number", desc: "How many are still in the queue — staff only", sample: "9" },
  { id: "longest_wait", label: "Longest wait", labelKey: "messaging.token.longest_wait.label", group: "queue", type: "number", desc: "Minutes the longest-waiting person has been there — staff only", sample: "38" },
  { id: "affected_count", label: "Bookings affected", labelKey: "messaging.token.affected_count.label", group: "org", type: "number", desc: "Bookings inside a closure window — staff only", sample: "14" },
  { id: "customers_notified", label: "Customers notified", labelKey: "messaging.token.customers_notified.label", group: "org", type: "number", desc: "How many were messaged automatically — staff only", sample: "14" },
  { id: "closure_reason", label: "Closure reason", labelKey: "messaging.token.closure_reason.label", group: "org", type: "text", desc: "Why a location is closed — staff only", sample: "Burst pipe on the ground floor" },
  { id: "closure_from", label: "Closure from", labelKey: "messaging.token.closure_from.label", group: "org", type: "date", desc: "First day of the closure", sample: "Thursday 9 October" },
  { id: "closure_to", label: "Closure to", labelKey: "messaging.token.closure_to.label", group: "org", type: "date", desc: "Last day of the closure", sample: "Friday 10 October" },
  { id: "work_queue_url", label: "Work queue (staff)", labelKey: "messaging.token.work_queue_url.label", group: "org", type: "link", desc: "Where rebooking and decisions wait — staff only", sample: "https://jrni.example/s/work" },
];

export const GROUP_LABEL: Record<TokenGroup, string> = {
  person: "Person", appointment: "Appointment", event: "Event", queue: "Queue", org: "Organisation",
};
/** Which resource a message is attached to — decides what can resolve. */
export type MsgContext = "appointment" | "event" | "queue" | "unknown";
/** person + org always resolve; the rest depend on what the message is attached to. */
export function tokensFor(ctx: MsgContext): TokenDef[] {
  return TOKENS.filter((tk) => tk.group === "person" || tk.group === "org" || tk.group === ctx);
}
export function unavailableFor(ctx: MsgContext): TokenDef[] {
  return TOKENS.filter((tk) => tk.group !== "person" && tk.group !== "org" && tk.group !== ctx);
}
/** Infer context from the message id/name — the seeded ids carry their family. */
export function contextOf(idOrName: string): MsgContext {
  const s = (idOrName || "").toLowerCase();
  if (s.indexOf("msg_q_") === 0 || s.indexOf("queue") >= 0) return "queue";
  if (s.indexOf("msg_b_") === 0 || s.indexOf("booking") >= 0 || s.indexOf("appointment") >= 0) return "appointment";
  if (s.indexOf("msg_") === 0 || s.indexOf("event") >= 0) return "event";
  return "unknown";
}

export const tokenDef = (id: string) => TOKENS.find((tk) => tk.id === id) || null;

/** LINK tokens for a context, paired long-with-short — the vocabulary a Button's
 *  target picker offers (§4). The pair is ONE entry carrying its short sibling
 *  so the picker can render a length choice; where no short form exists the
 *  entry says why rather than showing a toggle that does nothing. */
export interface LinkTokenChoice {
  id: string; label: string; desc: string; sample: string; group: TokenGroup;
  shortId?: string; noShortReason?: string; systemIssued?: boolean;
}
export function linkTokenChoices(ctx: MsgContext): LinkTokenChoice[] {
  const inScope = tokensFor(ctx).filter((tk) => tk.type === "link");
  const shortByBase: Record<string, string> = {};
  inScope.forEach((tk) => { if (tk.shortOf) shortByBase[tk.shortOf] = tk.id; });
  return inScope
    .filter((tk) => !tk.shortOf)
    .map((tk) => ({
      id: tk.id, label: tk.label, desc: tk.desc, sample: tk.sample, group: tk.group,
      ...(shortByBase[tk.id] ? { shortId: shortByBase[tk.id] } : {}),
      ...(tk.noShortReason ? { noShortReason: tk.noShortReason } : {}),
      ...(tk.systemIssued ? { systemIssued: true } : {}),
    }));
}
/** {{tok}} / {{tok | fallback}} — and legacy {tok}. */
const TOKEN_RE = /\{\{\s*([a-z0-9_]+)\s*(?:\|\s*([^}]*?)\s*)?\}\}|\{\s*([a-z0-9_]+)\s*\}/gi;
export interface FoundToken { id: string; fallback?: string; raw: string; index: number; known: boolean }
export function findTokens(text: string): FoundToken[] {
  const out: FoundToken[] = [];
  const re = new RegExp(TOKEN_RE);
  let m: RegExpExecArray | null;
  while ((m = re.exec(text || "")) !== null) {
    const id = (m[1] || m[3] || "").toLowerCase();
    out.push({ id, fallback: m[2], raw: m[0], index: m.index, known: !!tokenDef(id) });
  }
  return out;
}
export const unknownTokens = (text: string) => findTokens(text).filter((f) => !f.known).map((f) => f.id);
export const hasTokens = (text: string) => findTokens(text).length > 0;
/** Render with sample values — what the preview shows. */
export function resolveSamples(text: string): string {
  return (text || "").replace(new RegExp(TOKEN_RE), (raw, a, fb, b) => {
    const d = tokenDef((a || b || "").toLowerCase());
    if (!d) return raw;
    return d.blankable && fb ? String(fb) : d.sample;
  });
}
/** SMS segments from the RESOLVED text — a token's real length is unknown until
 *  send, so the counter is presented as an estimate wherever tokens appear.
 *
 *  The arithmetic moved to `sms-encoding` (2026-08-31). What was here counted
 *  `length / 160`, which is wrong in the expensive direction twice over: it prices
 *  a `€` at one septet instead of two, and it prices a body containing ONE
 *  non-GSM-7 character — a typographic quote is the usual one — at 160 per
 *  segment when the real capacity has dropped to 70. Same shape, same callers. */
export function smsSegments(text: string): SmsAnalysis & { estimated: boolean } {
  return { ...analyzeSms(resolveSamples(text)), estimated: hasTokens(text) };
}
/** Named → Meta's numbered placeholders, in first-appearance order. */
export function compileForWhatsApp(text: string): { body: string; map: { n: number; id: string; sample: string }[] } {
  const order: string[] = [];
  const body = (text || "").replace(new RegExp(TOKEN_RE), (raw, a, _fb, b) => {
    const id = (a || b || "").toLowerCase();
    if (!tokenDef(id)) return raw;
    let i = order.indexOf(id);
    if (i < 0) { order.push(id); i = order.length - 1; }
    return `{{${i + 1}}}`;
  });
  return { body, map: order.map((id, i) => ({ n: i + 1, id, sample: (tokenDef(id) || { sample: "" }).sample })) };
}

/* ── The field ─────────────────────────────────────────────────────────── */

/* The panel is PORTALLED to <body> and positioned from the trigger's box (CC 0052 §1).
   It used to be `position: absolute` inside the field wrapper, where `zIndex: 40` only
   ranked it inside whatever stacking context an ancestor had created — the editor card
   won, and an ancestor `overflow` clipped it at the card edge. A z-index alone fixes
   neither; leaving the layout flow fixes both. */
const MENU_W = 288;
/* 9500 = the value `shell/MultiSelect` uses for its portalled menu; above the
   drawer (300) and modal (320) the composer can be inside. */
const menuWrap: React.CSSProperties = { position: "fixed", zIndex: 9500, width: MENU_W, minWidth: MENU_W, overflowY: "auto", background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: T.shadowLg, padding: 6 };

/** Track the anchor's viewport box; flip above when there is more room there. */
function useAnchoredBox(anchorRef: React.RefObject<HTMLElement | null>, align: "left" | "right") {
  const [box, setBox] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  useLayoutEffect(() => {
    const measure = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const gap = 6, edge = 8;
      const below = window.innerHeight - r.bottom - gap - edge;
      const above = r.top - gap - edge;
      const useBelow = below >= 220 || below >= above;
      const maxHeight = Math.max(160, Math.min(292, useBelow ? below : above));
      const left = Math.min(Math.max(edge, align === "right" ? r.right - MENU_W : r.left), window.innerWidth - MENU_W - edge);
      setBox({ top: useBelow ? r.bottom + gap : Math.max(edge, r.top - gap - maxHeight), left, maxHeight });
    };
    measure();
    window.addEventListener("resize", measure);
    /* capture: catches scrolling of any ancestor, not just the page. */
    window.addEventListener("scroll", measure, true);
    return () => { window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); };
  }, [anchorRef, align]);
  return box;
}

/** Shared token picker list. `filterType` narrows to one token type (link fields). */
function TokenMenu({ ctx, filterType, query, onPick, onClose, anchorRef, align, menuRef }: {
  ctx: MsgContext; filterType?: TokenType; query: string;
  onPick: (tk: TokenDef, fallback?: string) => void; onClose: () => void;
  /** Positioned from this element's box; the panel itself lives at the document root. */
  anchorRef: React.RefObject<HTMLElement | null>; align?: "left" | "right";
  /** So the trigger's blur handler can tell "focus went into the panel" from "focus left". */
  menuRef: React.RefObject<HTMLDivElement | null>;
}) {
  const t = useT();
  const [q, setQ] = useState(query);
  const [fbFor, setFbFor] = useState<TokenDef | null>(null);
  const [fbVal, setFbVal] = useState("");
  useEffect(() => setQ(query), [query]);
  const avail = tokensFor(ctx).filter((tk) => (!filterType || tk.type === filterType));
  const needle = q.trim().toLowerCase();
  const list = avail.filter((tk) => !needle || tk.id.indexOf(needle) >= 0 || t(tk.labelKey, tk.label).toLowerCase().indexOf(needle) >= 0);
  const groups: TokenGroup[] = ["person", "appointment", "event", "queue", "org"];
  const missing = unavailableFor(ctx).filter((tk) => !filterType || tk.type === filterType);
  const box = useAnchoredBox(anchorRef, align || "left");
  /* Out of the flow means the field's own blur no longer covers every dismissal —
     a click on inert page area moves no focus. Same listener MultiSelect uses. */
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const n = e.target as Node;
      if (menuRef.current && menuRef.current.contains(n)) return;
      if (anchorRef.current && anchorRef.current.contains(n)) return;
      onClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  });
  return createPortal(
    <div ref={menuRef} data-testid="token-menu" onClick={(e) => e.stopPropagation()}
      style={{ ...menuWrap, top: box ? box.top : 0, left: box ? box.left : 0, maxHeight: box ? box.maxHeight : 292, visibility: box ? "visible" : "hidden" }}>
      {fbFor ? (
        <div style={{ padding: 8 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: T.n1 }}>{t(fbFor.labelKey, fbFor.label)}</div>
          <div style={{ fontSize: 11.5, color: T.soft, margin: "4px 0 8px", lineHeight: 1.45 }}>{t("messaging.tokens.fallbackHelp", "Shown when this is empty for a recipient. Leave blank for no fallback.")}</div>
          <input autoFocus value={fbVal} onChange={(e) => setFbVal(e.target.value)} placeholder={t("messaging.tokens.fallbackPlaceholder", "e.g. there")}
            onKeyDown={(e) => { if (e.key === "Enter") { onPick(fbFor, fbVal.trim() || undefined); setFbFor(null); } if (e.key === "Escape") setFbFor(null); }}
            style={{ width: "100%", height: 32, padding: "0 9px", border: `1px solid ${T.n4}`, borderRadius: 7, fontSize: 13, fontFamily: T.font, boxSizing: "border-box" }} />
          <div style={{ display: "flex", gap: 6, marginTop: 8, justifyContent: "flex-end" }}>
            <Btn size="sm" variant="secondary" onClick={() => setFbFor(null)}>{t("common.actions.cancel", "Cancel")}</Btn>
            <Btn size="sm" onClick={() => { onPick(fbFor, fbVal.trim() || undefined); setFbFor(null); }}>{t("messaging.tokens.insert", "Insert")}</Btn>
          </div>
        </div>
      ) : (
        <>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("messaging.tokens.search", "Search tokens…")}
            onKeyDown={(e) => { if (e.key === "Escape") onClose(); if (e.key === "Enter" && list.length) onPick(list[0]); }}
            style={{ width: "100%", height: 32, padding: "0 9px", border: `1px solid ${T.n4}`, borderRadius: 7, fontSize: 13, fontFamily: T.font, marginBottom: 6, boxSizing: "border-box" }} />
          {list.length === 0 ? <div style={{ padding: "10px 8px", fontSize: 12.5, color: T.soft }}>{t("messaging.tokens.none", "No token matches that.")}</div> : null}
          {groups.map((g) => {
            const rows = list.filter((tk) => tk.group === g);
            if (!rows.length) return null;
            return (
              <div key={g}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.04, textTransform: "uppercase", color: T.soft, padding: "8px 8px 4px" }}>{GROUP_LABEL[g]}</div>
                {rows.map((tk) => (
                  <button key={tk.id} type="button" data-testid="token-option" title={tk.desc}
                    onClick={() => (tk.blankable ? (setFbFor(tk), setFbVal("")) : onPick(tk))}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "7px 8px", border: "none", borderRadius: 7, background: "transparent", cursor: "pointer", fontFamily: T.font }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n1 }}>{t(tk.labelKey, tk.label)}</span>
                      {tk.blankable ? <span style={{ fontSize: 9.5, fontWeight: 700, color: T.orange1, background: T.orange2, borderRadius: 4, padding: "1px 4px" }}>{t("messaging.tokens.canBeBlank", "CAN BE BLANK")}</span> : null}
                    </div>
                    {/* The sample is the useful line — the raw id repeats the label (CC 0052 §2). */}
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 2 }}>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: T.n2, lineHeight: 1.35, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tk.sample}</span>
                      <span style={{ flexShrink: 0, fontSize: 10, color: T.n3, fontFamily: T.fontMono }}>{`{{${tk.id}}}`}</span>
                    </div>
                  </button>
                ))}
              </div>
            );
          })}
          {missing.length && !needle ? (
            <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 6, padding: "8px 8px 4px" }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.04, textTransform: "uppercase", color: T.n3 }}>{t("messaging.tokens.unavailable", "Not available here")}</div>
              <div style={{ fontSize: 11, color: T.n3, lineHeight: 1.5, marginTop: 4 }}>{missing.slice(0, 6).map((tk) => t(tk.labelKey, tk.label)).join(" · ")}{missing.length > 6 ? " …" : ""}</div>
              <div style={{ fontSize: 11, color: T.soft, marginTop: 5, lineHeight: 1.45 }}>{t("messaging.tokens.unavailableWhy", "These would resolve to nothing for this message, so they are not offered.")}</div>
            </div>
          ) : null}
          <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 6, padding: "7px 8px 3px", fontSize: 10.5, color: T.n3, lineHeight: 1.4 }}>{t("messaging.tokens.provisional", "Provisional list — the sendable set comes from the backend (REQ-115).")}</div>
        </>
      )}
    </div>,
    document.body,
  );
}

/**
 * A text input/textarea with all three approved insertion affordances:
 * type `{{` for autocomplete at the caret, an Insert-token button, and optional
 * quick chips. Value stays plain text — no chip editor (see the proposal).
 */
export function TokenField({ value, onChange, ctx, multiline, rows, maxLength, placeholder, ariaRequired, invalid, style, quick, filterType, testid }: {
  value: string; onChange: (v: string) => void; ctx: MsgContext;
  multiline?: boolean; rows?: number; maxLength?: number; placeholder?: string;
  ariaRequired?: boolean; invalid?: boolean; style?: React.CSSProperties;
  /** Token ids offered as one-click chips under the field. */
  quick?: string[]; filterType?: TokenType; testid?: string;
}) {
  const t = useT();
  const ref = useRef<HTMLTextAreaElement & HTMLInputElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState<null | { query: string; fromCaret: boolean }>(null);
  const unknown = unknownTokens(value);

  const insertAt = (text: string) => {
    const el = ref.current;
    const start = el && typeof el.selectionStart === "number" ? el.selectionStart : value.length;
    const end = el && typeof el.selectionEnd === "number" ? el.selectionEnd : value.length;
    const next = value.slice(0, start) + text + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => { if (el) { el.focus(); const p = start + text.length; try { el.setSelectionRange(p, p); } catch { /* input types without selection */ } } });
  };
  /** Insertion from the caret path replaces the trigger the user typed. */
  const commit = (tk: TokenDef, fallback?: string) => {
    const tok = `{{${tk.id}${fallback ? ` | ${fallback}` : ""}}}`;
    const el = ref.current;
    if (menu?.fromCaret && el && typeof el.selectionStart === "number") {
      const caret = el.selectionStart;
      const before = value.slice(0, caret);
      const trig = before.lastIndexOf("{{");
      if (trig >= 0) {
        const next = value.slice(0, trig) + tok + value.slice(caret);
        onChange(next);
        setMenu(null);
        requestAnimationFrame(() => { if (el) { el.focus(); const p = trig + tok.length; try { el.setSelectionRange(p, p); } catch { /* noop */ } } });
        return;
      }
    }
    insertAt(tok);
    setMenu(null);
  };
  /** Typing `{{` opens the menu; whatever follows filters it. */
  const onInput = (v: string) => {
    onChange(v);
    const el = ref.current;
    const caret = el && typeof el.selectionStart === "number" ? el.selectionStart : v.length;
    const before = v.slice(0, caret);
    const trig = before.lastIndexOf("{{");
    if (trig >= 0 && before.indexOf("}}", trig) < 0) {
      const q = before.slice(trig + 2);
      if (q.length <= 24 && !/[{}\n]/.test(q)) { setMenu({ query: q, fromCaret: true }); return; }
    }
    if (menu?.fromCaret) setMenu(null);
  };

  const common = {
    ref: ref as never, value, placeholder,
    "aria-required": ariaRequired ? ("true" as const) : undefined,
    "aria-invalid": invalid ? ("true" as const) : undefined,
    "data-testid": testid,
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => onInput(e.target.value),
    onKeyDown: (e: React.KeyboardEvent) => { if (e.key === "Escape" && menu) { e.stopPropagation(); setMenu(null); } },
    style: { width: "100%", padding: multiline ? 12 : "0 12px", height: multiline ? "auto" : 40, border: `1px solid ${invalid ? T.red1 : T.n4}`, borderRadius: 8, background: T.white, color: T.n1, fontSize: 14, fontFamily: T.font, outline: "none", boxSizing: "border-box" as const, lineHeight: 1.5, resize: multiline ? ("vertical" as const) : undefined, ...(style || {}) },
  };

  return (
    /* The panel is portalled, so "focus is still in the widget" now means the wrapper
       OR the panel — without the second test, the panel's autofocused search input
       would blur the field and close the panel it just opened. */
    <div style={{ position: "relative" }} onBlur={(e) => { const rt = e.relatedTarget as Node | null; if (!e.currentTarget.contains(rt) && !(menuRef.current && rt && menuRef.current.contains(rt))) setMenu(null); }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ flex: 1 }} />
        <button ref={btnRef} type="button" onClick={() => setMenu(menu && !menu.fromCaret ? null : { query: "", fromCaret: false })} data-testid="token-insert-btn"
          style={{ display: "inline-flex", alignItems: "center", gap: 5, minHeight: 26, padding: "0 9px", borderRadius: 7, border: `1px solid ${T.border}`, background: T.white, color: T.p1, fontFamily: T.font, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
          <Icon.plus size={11} />{t("messaging.tokens.insertToken", "Insert token")}
        </button>
      </div>
      {multiline
        ? <textarea {...common} rows={rows || 6} maxLength={maxLength} />
        : <input {...common} maxLength={maxLength} />}
      {menu ? (
        <TokenMenu ctx={ctx} filterType={filterType} query={menu.query} onPick={commit} onClose={() => setMenu(null)}
          anchorRef={menu.fromCaret ? ref : btnRef} align={menu.fromCaret ? "left" : "right"} menuRef={menuRef} />
      ) : null}
      {quick && quick.length ? (
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 7 }}>
          {quick.map((id) => {
            const tk = tokenDef(id);
            if (!tk) return null;
            return (
              <button key={id} type="button" onClick={() => insertAt(`{{${tk.id}}}`)} data-testid="token-chip"
                style={{ minHeight: 24, padding: "0 9px", borderRadius: 999, border: `1px solid ${T.p3}`, background: T.p4, color: T.p1, fontFamily: T.font, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>{tk.label}</button>
            );
          })}
        </div>
      ) : null}
      {unknown.length ? (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, color: T.red1, marginTop: 6 }}>
          <Icon.warn size={12} />{t("messaging.tokens.unknown", "No such token:")} {unknown.slice(0, 3).map((u) => `{{${u}}}`).join(", ")}
        </div>
      ) : null}
    </div>
  );
}

/** Small inline picker for fields that are not TokenFields (e.g. block URLs). */
export function TokenButton({ ctx, filterType, onPick, label }: { ctx: MsgContext; filterType?: TokenType; onPick: (tokenText: string) => void; label?: string }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  return (
    <div style={{ position: "relative" }} onBlur={(e) => { const rt = e.relatedTarget as Node | null; if (!e.currentTarget.contains(rt) && !(menuRef.current && rt && menuRef.current.contains(rt))) setOpen(false); }}>
      <button ref={btnRef} type="button" onClick={() => setOpen(!open)} data-testid="token-insert-btn"
        style={{ display: "inline-flex", alignItems: "center", gap: 5, minHeight: 26, padding: "0 9px", borderRadius: 7, border: `1px solid ${T.border}`, background: T.white, color: T.p1, fontFamily: T.font, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
        <Icon.plus size={11} />{label || t("messaging.tokens.insertToken", "Insert token")}
      </button>
      {open ? <TokenMenu ctx={ctx} filterType={filterType} query="" onClose={() => setOpen(false)} anchorRef={btnRef} align="right" menuRef={menuRef}
        onPick={(tk, fb) => { onPick(`{{${tk.id}${fb ? ` | ${fb}` : ""}}}`); setOpen(false); }} /> : null}
    </div>
  );
}

/** Sample-resolved preview text + the toggle label, shared by both previews. */
export function usePreviewTokens(): { showRaw: boolean; toggle: () => void; render: (s: string) => string; Toggle: () => JSX.Element } {
  const t = useT();
  const [showRaw, setShowRaw] = useState(false);
  const toggle = () => setShowRaw((v) => !v);
  const Toggle = () => (
    <button type="button" onClick={toggle} data-testid="token-preview-toggle"
      style={{ minHeight: 24, padding: "0 9px", borderRadius: 999, border: `1px solid ${showRaw ? T.n1 : T.border}`, background: showRaw ? T.n1 : T.white, color: showRaw ? T.white : T.n2, fontFamily: T.font, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
      {showRaw ? t("messaging.tokens.showValues", "Show values") : t("messaging.tokens.showTokens", "Show tokens")}
    </button>
  );
  return { showRaw, toggle, render: (s: string) => (showRaw ? s : resolveSamples(s)), Toggle };
}
